import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../constants/color";
import { Icon } from "../icons";
import { url } from "../constants/url";
import axios from "axios";
import { useAuth } from "../context/authContext";
import CustomTooltip from "../components/CustomTooltip";
import AppToast from "../components/AppToast";
import EngineerDetailsSidebar from "../components/engineers/EngineerDetailsSidebar";
import EngineerStatusUpdateModal from "../components/engineers/EngineerStatusUpdateModal";
import EngineerDeleteModal from "../components/engineers/EngineerDeleteModal";
import EngineerEditModal from "../components/engineers/EngineerEditModal";
const statusStyles = {
  Active: {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  APPROVED: {
    bgcolor: "#8CD47E",
    color: "#ffffff",
  },
  Pending: {
    bgcolor: "#F8D66D",
    color: "#ffffff",
  },
  PENDING: {
    bgcolor: "#F8D66D",
    color: "#ffffff",
  },
  Rejected: {
    bgcolor: "#FF6961",
    color: "#ffffff",
  },
  REJECTED: {
    bgcolor: "#FF6961",
    color: "#ffffff",
  },
};

const getRejectedCellStyle = (status) => {
  return status === "REJECTED" ? {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      top: '50%',
      borderTop: '1px solid #FF6961',
      zIndex: 1,
      pointerEvents: 'none'
    }
  } : {};
};

const Engineers = () => {
  const { token } = useAuth();
  const [engineersData, setEngineersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEngineers, setTotalEngineers] = useState(0);
  const [pageSize] = useState(12);

  // Filter states
  const [viewAnchorEl, setViewAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [nameSortAnchorEl, setNameSortAnchorEl] = useState(null);
  const [emailSortAnchorEl, setEmailSortAnchorEl] = useState(null);
  const [selectedView, setSelectedView] = useState("ALL");
  const [selectedSort, setSelectedSort] = useState("LATEST");
  const [nameSort, setNameSort] = useState(null);
  const [emailSort, setEmailSort] = useState(null);

  // Modal states
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  // Helper function to show toast
  const showToast = (type, title, message) => {
    setToast({
      open: true,
      type: type,
      title: title,
      message: message,
    });
  };

  // Helper function to hide toast
  const hideToast = () => {
    setToast({ ...toast, open: false });
  };

  // Load deleted engineer IDs from localStorage
  const getDeletedEngineerIds = () => {
    try {
      const saved = localStorage.getItem("deletedEngineers");
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error("Error loading deleted engineers:", error);
      return [];
    }
  };
  const handleEditClick = (engineer) => {
    setSelectedEngineer(engineer);
    setEditModalOpen(true);
  };
  const [, setDeletedEngineerIds] = useState(getDeletedEngineerIds());

  // Map sort values for engineers
  const mapSortToApi = (sortValue) => {
    switch (sortValue) {
      case "LATEST":
        return "latest";
      case "OLDEST":
        return "oldest";
      case "AZ":
        return "az";
      case "ZA":
        return "za";
      case "COMPLETED_HIGHEST":
        return "completedHighest";
      case "COMPLETED_LOWEST":
        return "completedLowest";
      case "ASSIGNED_HIGHEST":
        return "assignedHighest";
      case "ASSIGNED_LOWEST":
        return "assignedLowest";
      case "QUESTIONS_HIGHEST":
        return "questionsHighest";
      case "QUESTIONS_LOWEST":
        return "questionsLowest";
      case "YES_HIGHEST":
        return "yesHighest";
      case "YES_LOWEST":
        return "yesLowest";
      case "CRITICAL_HIGHEST":
        return "criticalHighest";
      case "CRITICAL_LOWEST":
        return "criticalLowest";
      default:
        return "latest";
    }
  };

  const mapViewToApi = (viewValue) => {
    switch (viewValue) {
      case "APPROVED":
        return "APPROVED";
      case "PENDING":
        return "PENDING";
      case "REJECTED":
        return "REJECTED";
      case "ALL":
        return null;
      default:
        return null;
    }
  };

  const isFilterApplied = () => {
    return (
      selectedView !== "ALL" ||
      selectedSort !== "LATEST" ||
      nameSort !== null ||
      emailSort !== null ||
      searchQuery !== ""
    );
  };

  // Function to extract project data from engineer object
  const getProjectCounts = (engineer) => {
    if (!engineer) {
      return {
        completed: 0,
        assigned: 0,
        display: "0/0",
        tooltip: "Completed: 0\nAssigned: 0",
      };
    }

    // Check for projectsRecords structure
    let completed = 0;
    let assigned = 0;

    if (engineer.projectsRecords) {
      completed = engineer.projectsRecords.projectCompleted || 0;
      assigned = engineer.projectsRecords.projectAssigned || 0;
    }

    return {
      completed,
      assigned,
      display: `${completed}/${assigned}`,
      tooltip: `Completed: ${completed}\nAssigned: ${assigned}`,
    };
  };

  //   const getLocation = (engineer) => {
  //   if (!engineer) return "-";

  //   const city = formatCity(engineer.city);
  //   const state = formatStateCode(engineer.state);

  //   if (city && state) return `${city}, ${state}`;
  //   if (city) return city;
  //   if (state) return state;

  //   return engineer.detailedAddress || "-";
  // };

  // Function to get location from engineer object
  const getLocation = (engineer) => {
    if (!engineer) return "-";

    const locationParts = [];
    if (engineer.city) locationParts.push(engineer.city);
    if (engineer.state) locationParts.push(engineer.state);

    if (locationParts.length > 0) {
      return locationParts.join(", ");
    }

    // Fallback to detailedAddress
    if (engineer.detailedAddress) {
      return engineer.detailedAddress;
    }

    return "-";
  };

  // Fetch engineers from API
  const fetchEngineers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        role: "ENGINEER",
        page: page,
        limit: pageSize,
      };

      if (selectedSort !== "LATEST") {
        params.sort = mapSortToApi(selectedSort);
      }

      if (nameSort === "AZ") {
        params.sort = "az";
      } else if (nameSort === "ZA") {
        params.sort = "za";
      } else if (emailSort === "AZ") {
        params.sort = "az";
      } else if (emailSort === "ZA") {
        params.sort = "za";
      }

      const statusFilter = mapViewToApi(selectedView);
      if (statusFilter) {
        params.status = statusFilter;
      }

      if (searchQuery) {
        params.search = searchQuery;
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params,
      };

      console.log("Fetching engineers with params:", params);

      const response = await axios.get(`${url}/api/users/fetch`, config);

      if (response.data.users) {
        console.log("Received engineers data:", response.data.users);

        // Get current deleted IDs from localStorage
        const currentDeletedIds = getDeletedEngineerIds();

        // Filter out deleted engineers
        const filteredEngineers = response.data.users.filter(
          (engineer) => !currentDeletedIds.includes(engineer._id),
        );

        setEngineersData(filteredEngineers);

        // Use total from backend response
        const totalFromBackend = response.data.total || 0;
        const activeCount = totalFromBackend - currentDeletedIds.length;

        setTotalEngineers(Math.max(0, activeCount));
        setTotalPages(Math.ceil(totalFromBackend / pageSize));
      } else {
        setEngineersData([]);
        setTotalEngineers(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Error fetching engineers:", err);
      setError("Failed to fetch engineers data");
      showToast(
        "error",
        "Fetch Failed",
        "Failed to fetch engineers data. Please try again.",
      );
      setEngineersData([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, token, selectedSort, selectedView,    nameSort,
    emailSort,
    searchQuery,
  ]);

  // API call to update engineer status
  const updateEngineerStatus = async (engineerId, newStatus) => {
    try {
      setActionLoading(true);

      // Find engineer for name
      const engineer = engineersData.find((e) => e._id === engineerId);
      const engineerName = engineer?.name || "Engineer";
      const currentStatus = getStatusText(engineer?.status || "PENDING");

      // Map frontend status to backend status
      const statusMap = {
        Active: "APPROVED",
        Rejected: "REJECTED",
        Pending: "PENDING",
      };

      const backendStatus = statusMap[newStatus] || newStatus.toUpperCase();

      const response = await axios.post(
        `${url}/api/admin/approve-req`,
        {
          userID: engineerId,
          action: backendStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 200) {
        showToast(
          "success",
          "Status Updated",
          `<strong>${engineerName}'s </strong> status changed from <strong>${currentStatus}</strong> to <strong>${newStatus} </strong>`,
        );

        // Refresh the engineers list
        fetchEngineers();
      }
    } catch (err) {
      console.error("Error updating engineer status:", err);
      const engineer = engineersData.find((e) => e._id === engineerId);
      const engineerName = engineer?.name || "Engineer";
      showToast(
        "error",
        "Update Failed",
        `Failed to update <strong>${engineerName}'s </strong> status: ${
          err.response?.data?.message || err.message
        }`,
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Handle engineer deletion with name in toast
  const handleDeleteEngineer = async (engineer) => {
    if (!engineer || !engineer._id) {
      console.error("No engineer ID found for deletion");
      showToast("error", "Error", "Cannot remove engineer - no ID found");
      return;
    }

    const engineerName = engineer.name || "Engineer";

    try {
      setActionLoading(engineer._id + "delete");
      console.log("Deleting engineer:", engineer._id);

      // Call backend API to delete user
      const response = await axios.delete(
        `${url}/api/users/delete/${engineer._id}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        },
      );

      console.log("Delete response:", response.data);

      if (response.data.success || response.status === 200) {
        // Add to deleted IDs list in localStorage for reference
        const currentDeletedIds = getDeletedEngineerIds();
        if (!currentDeletedIds.includes(engineer._id)) {
          const updatedDeletedIds = [...currentDeletedIds, engineer._id];
          localStorage.setItem(
            "deletedEngineers",
            JSON.stringify(updatedDeletedIds),
          );
          setDeletedEngineerIds(updatedDeletedIds);
        }

        // Remove from current view
        setEngineersData((prevEngineers) =>
          prevEngineers.filter((e) => e._id !== engineer._id),
        );

        // Update total count
        setTotalEngineers((prev) => Math.max(0, prev - 1));

        // Adjust page if needed
        if (engineersData.length === 1 && page > 1) {
          setPage((prev) => prev - 1);
        }

        // Show success message with name
        showToast(
          "success",
          "Engineer Deleted",
          `<strong>${engineerName}</strong> has been permanently deleted from the database`,
        );
      }
    } catch (error) {
      console.error("Error deleting engineer:", error);
      console.error("Error response:", error.response?.data);
      const engineerName = engineer?.name || "Engineer";
      showToast(
        "error",
        "Error",
        `Error deleting ${engineerName}: ${
          error.response?.data?.message || error.message
        }`,
      );
    } finally {
      setActionLoading(null);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchEngineers();
  }, [fetchEngineers]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Calculate displayed range
  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, totalEngineers);

  // Get status text for display
  const getStatusText = (status) => {
    switch (status) {
      case "APPROVED":
        return "Active";
      case "PENDING":
        return "Pending";
      case "REJECTED":
        return "Rejected";
      default:
        return status || "Pending";
    }
  };
  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedView("ALL");
    setSelectedSort("LATEST");
    setNameSort(null);
    setEmailSort(null);
    setSearchQuery("");
    setPage(1);
  };

  // Handle Name column sort
  const handleNameSort = (sortType) => {
    setNameSort(sortType);
    setEmailSort(null);
    setNameSortAnchorEl(null);
    setPage(1);
  };

  // Handle Email column sort
  const handleEmailSort = (sortType) => {
    setEmailSort(sortType);
    setNameSort(null);
    setEmailSortAnchorEl(null);
    setPage(1);
  };

  // Handle status change via modal
  const handleStatusUpdate = (selectedStatus) => {
    if (selectedEngineer) {
      updateEngineerStatus(selectedEngineer._id, selectedStatus);
      setStatusModalOpen(false);
      setSelectedEngineer(null);
    }
  };

  // Handle view details
  const handleViewDetails = (engineer) => {
    setSelectedEngineer(engineer);
    setIsSidebarOpen(true);
  };

  // Handle status click
  const handleStatusClick = (engineer) => {
    setSelectedEngineer(engineer);
    setStatusModalOpen(true);
  };

  // Handle delete click
  const handleDeleteClick = (engineer) => {
    setSelectedEngineer(engineer);
    setDeleteOpen(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = (engineer) => {
    handleDeleteEngineer(engineer);
    setDeleteOpen(false);
    setSelectedEngineer(null);
  };

  if (loading && engineersData.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H2,
              fontFamily: FONT_FAMILY,
              color: COLORS.PRIMARY,
            }}
          >
            Engineers
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') fetchEngineers(); }}
            placeholder="Search..."
            size="small"
            variant="outlined"
            sx={{
              background: "#fff",
              borderRadius: 2,
              width: { xs: "100%", md: 240 },
              "& .MuiOutlinedInput-root": {
                height: "36px",
                "& fieldset": { borderColor: COLORS.LIGHT_SECONDARY },
                "&:hover fieldset": { borderColor: COLORS.SECONDARY },
                "&.Mui-focused fieldset": { borderColor: COLORS.SECONDARY },
              },
            }}
          />
          <CustomTooltip title="Sort">
            <IconButton onClick={(e) => setSortAnchorEl(e.currentTarget)}>
              <Icon name="sort" color={COLORS.SECONDARY} />
            </IconButton>
          </CustomTooltip>
          <CustomTooltip title="View">
            <IconButton onClick={(e) => setViewAnchorEl(e.currentTarget)}>
              <Icon name="list" color={COLORS.SECONDARY} />
            </IconButton>
          </CustomTooltip>
          {isFilterApplied() && (
            <CustomTooltip title="Clear Filters">
              <IconButton onClick={clearFilters}>
                <Icon name="rotate" color={COLORS.ERROR} />
              </IconButton>
            </CustomTooltip>
          )}
        </Box>
      </Box>

      {/* Error Message */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper elevation={0} sx={{ backgroundColor: "transparent" }}>
        <TableContainer>
          <Table
            sx={{
              backgroundColor: "transparent",
              "& thead th": {
                backgroundColor: "transparent",
              },
              "& tbody td": {
                backgroundColor: "transparent",
                borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
              },
            }}
          >
            <TableHead>
              <TableRow>
                {[
                  { key: "Name", hasSort: true },
                  { key: "Email Address", hasSort: true },
                  { key: "Phone", hasSort: false },
                  { key: "Status", hasSort: false },
                  { key: "Location", hasSort: false },
                  { key: "Actions", hasSort: false },
                ].map((head) => (
                  <TableCell
                    key={head.key}
                    align={head.key === "Field Inspection Stats" || head.key === "Counts" ? "center" : "left"}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      color: COLORS.PRIMARY,
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      whiteSpace: "nowrap",
                      width: head.key === "Location" ? "180px" : head.key === "Field Inspection Stats" ? "150px" : "auto",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: head.key === "Field Inspection Stats" || head.key === "Counts" ? "center" : "space-between",
                        gap: 1,
                      }}
                    >
                      <span>{head.key}</span>
                      {head.hasSort && (
                        <CustomTooltip title="Sort">
                          <Icon
                            name="sort"
                            color={COLORS.LIGHT_SECONDARY}
                            sx={{
                              fontSize: 16,
                              cursor: "pointer",
                              color:
                                head.key === "Name" && nameSort
                                  ? COLORS.ERROR
                                  : head.key === "Email Address" && emailSort
                                    ? COLORS.ERROR
                                    : COLORS.LIGHT_SECONDARY,
                            }}
                            onClick={(e) => {
                              if (head.key === "Name") {
                                setNameSortAnchorEl(e.currentTarget);
                              } else if (head.key === "Email Address") {
                                setEmailSortAnchorEl(e.currentTarget);
                              }
                            }}
                          />
                        </CustomTooltip>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && engineersData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : engineersData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography sx={{ color: COLORS.TEXT }}>
                      No engineers found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                engineersData.map((engineer) => {
                  const projectInfo = getProjectCounts(engineer);
                  const frontendStatus = getStatusText(engineer.status);
                  const location = getLocation(engineer);

                  return (
                    <TableRow
                      key={engineer._id || engineer.id}
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      }}
                    >
                      {/* Name */}
                      <TableCell sx={{ ...getRejectedCellStyle(engineer.status) }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            alignItems: "center",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                ...TYPOGRAPHY.BODY_LARGE,
                                color: COLORS.TEXT,
                              }}
                            >
                              {engineer.name || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Email */}
                      <TableCell
                        sx={{ color: COLORS.TEXT, ...TYPOGRAPHY.BODY_LARGE, ...getRejectedCellStyle(engineer.status) }}
                      >
                        {engineer.email || "-"}
                      </TableCell>

                      {/* Phone */}
                      <TableCell
                        sx={{ color: COLORS.TEXT, ...TYPOGRAPHY.BODY_LARGE, ...getRejectedCellStyle(engineer.status) }}
                      >
                        {engineer.phone || "-"}
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={{ ...getRejectedCellStyle(engineer.status) }}>
                        <Chip
                          label={frontendStatus}
                          size="small"
                          sx={{
                            ...(statusStyles[engineer.status] ||
                              statusStyles.Pending),
                            ...TYPOGRAPHY.BODY_SMALL,
                            height: 22,
                          }}
                        />
                      </TableCell>

                      {/* Location */}
                      <TableCell
                        sx={{
                          color: COLORS.TEXT,
                          ...TYPOGRAPHY.BODY_LARGE,
                          ...getRejectedCellStyle(engineer.status),
                          width: "180px",
                        }}
                      >
                        {location}
                      </TableCell>

                      {/* Actions with Tooltips */}
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <CustomTooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(engineer)}
                            >
                              <Icon name="Action1" color={COLORS.TEXT} />
                            </IconButton>
                          </CustomTooltip>

                          <CustomTooltip title="Change Status">
                            <IconButton
                              size="small"
                              onClick={() => handleStatusClick(engineer)}
                              disabled={actionLoading}
                            >
                              <Icon name="Action2" color={COLORS.TEXT} />
                            </IconButton>
                          </CustomTooltip>
                          <CustomTooltip title="Edit Details">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(engineer)}
                            >
                              <Icon name="Action3" color={COLORS.PRIMARY} />
                            </IconButton>
                          </CustomTooltip>

                          <CustomTooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(engineer)}
                            >
                              <Icon name="Action4" color={COLORS.ERROR} />
                            </IconButton>
                          </CustomTooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {!loading && engineersData.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 2,
              p: 2,
            }}
          >
            <Typography sx={{ fontSize: 13, color: "#777" }}>
              {startIndex} - {endIndex} of {totalEngineers}
            </Typography>

            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                <Icon name="chevron-left" />
              </IconButton>

              {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                let pageNum;
                if (totalPages <= 3) {
                  pageNum = i + 1;
                } else if (page === 1) {
                  pageNum = i + 1;
                } else if (page === totalPages) {
                  pageNum = totalPages - 2 + i;
                } else {
                  pageNum = page - 1 + i;
                }

                if (pageNum > 0 && pageNum <= totalPages) {
                  return (
                    <Button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      sx={{
                        minWidth: 32,
                        height: 32,
                        bgcolor:
                          page === pageNum ? COLORS.PRIMARY : "transparent",
                        color: page === pageNum ? "#fff" : COLORS.TEXT,
                        border: page === pageNum ? "none" : "1px solid #E1BEE7",
                        "&:hover": {
                          bgcolor:
                            page === pageNum
                              ? COLORS.PRIMARY
                              : "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      {pageNum}
                    </Button>
                  );
                }
                return null;
              })}

              <IconButton
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
              >
                <Icon name="chevron-right" />
              </IconButton>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Name Column Sort Menu */}
      <Menu
        anchorEl={nameSortAnchorEl}
        open={Boolean(nameSortAnchorEl)}
        onClose={() => setNameSortAnchorEl(null)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
            px: 0.5,
          },
        }}
      >
        {[
          { label: "A to Z", value: "AZ", icon: "foldUp" },
          { label: "Z to A", value: "ZA", icon: "foldDown" },
        ].map((item) => {
          const isActive = nameSort === item.value;

          return (
            <MenuItem
              key={item.value}
              onClick={() => handleNameSort(item.value)}
              sx={{
                gap: 1.5,
                borderRadius: 1,
                bgcolor: "transparent !important",
                "&:hover": { bgcolor: "transparent" },
                "&.Mui-selected": { bgcolor: "transparent" },
                color: isActive ? COLORS.PRIMARY : COLORS.TEXT,
              }}
            >
              <Icon
                name={item.icon}
                size={18}
                color={isActive ? COLORS.PRIMARY : COLORS.TEXT}
              />
              <Typography sx={TYPOGRAPHY.BODY_LARGE}>{item.label}</Typography>
            </MenuItem>
          );
        })}
      </Menu>

      {/* Email Column Sort Menu */}
      <Menu
        anchorEl={emailSortAnchorEl}
        open={Boolean(emailSortAnchorEl)}
        onClose={() => setEmailSortAnchorEl(null)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
            px: 0.5,
          },
        }}
      >
        {[
          { label: "A to Z", value: "AZ", icon: "foldUp" },
          { label: "Z to A", value: "ZA", icon: "foldDown" },
        ].map((item) => {
          const isActive = emailSort === item.value;

          return (
            <MenuItem
              key={item.value}
              onClick={() => handleEmailSort(item.value)}
              sx={{
                gap: 1.5,
                borderRadius: 1,
                bgcolor: "transparent !important",
                "&:hover": { bgcolor: "transparent" },
                "&.Mui-selected": { bgcolor: "transparent" },
                color: isActive ? COLORS.PRIMARY : COLORS.TEXT,
              }}
            >
              <Icon
                name={item.icon}
                size={18}
                color={isActive ? COLORS.PRIMARY : COLORS.TEXT}
              />
              <Typography sx={TYPOGRAPHY.BODY_LARGE}>{item.label}</Typography>
            </MenuItem>
          );
        })}
      </Menu>

      {/* View Menu */}
      <Menu
        anchorEl={viewAnchorEl}
        open={Boolean(viewAnchorEl)}
        onClose={() => setViewAnchorEl(null)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
            px: 0.5,
          },
        }}
      >
        {[
          {
            label: "All",
            value: "ALL",
            icon: "usersGroup",
            color: COLORS.PRIMARY,
          },
          {
            label: "Active",
            value: "APPROVED",
            icon: "users",
            color: "#8CD47E",
          },
          {
            label: "Pending",
            value: "PENDING",
            icon: "users",
            color: "#F8D66D",
          },
          {
            label: "Rejected",
            value: "REJECTED",
            icon: "users",
            color: "#FF6961",
          },
        ].map((item) => {
          const isActive = selectedView === item.value;

          return (
            <MenuItem
              key={item.value}
              onClick={() => {
                setSelectedView(item.value);
                setViewAnchorEl(null);
              }}
              sx={{
                gap: 1.5,
                borderRadius: 1,
                bgcolor: "transparent !important",
                "&:hover": { bgcolor: "transparent" },
                "&.Mui-selected": { bgcolor: "transparent" },
                color: isActive ? item.color : COLORS.TEXT,
              }}
            >
              <Icon name={item.icon} size={18} color={item.color} />
              <Typography sx={TYPOGRAPHY.BODY_LARGE}>{item.label}</Typography>
            </MenuItem>
          );
        })}
      </Menu>

      {/* Global Sort Menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={() => setSortAnchorEl(null)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
            px: 0.5,
          },
        }}
      >
        {[
          { label: "Latest First", value: "LATEST", icon: "foldUp" },
          { label: "Oldest First", value: "OLDEST", icon: "foldDown" },
          { label: "A to Z", value: "AZ", icon: "foldUp" },
          { label: "Z to A", value: "ZA", icon: "foldDown" },
          {
            label: "Completed Highest",
            value: "COMPLETED_HIGHEST",
            icon: "foldUp",
          },
          {
            label: "Completed Lowest",
            value: "COMPLETED_LOWEST",
            icon: "foldDown",
          },
          {
            label: "Assigned Highest",
            value: "ASSIGNED_HIGHEST",
            icon: "foldUp",
          },
          {
            label: "Assigned Lowest",
            value: "ASSIGNED_LOWEST",
            icon: "foldDown",
          },
        ].map((item) => {
          const isActive = selectedSort === item.value;

          return (
            <MenuItem
              key={item.value}
              onClick={() => {
                setSelectedSort(item.value);
                setSortAnchorEl(null);
              }}
              sx={{
                gap: 1.5,
                borderRadius: 1,
                bgcolor: "transparent !important",
                "&:hover": { bgcolor: "transparent" },
                "&.Mui-selected": { bgcolor: "transparent" },
                color: isActive ? COLORS.PRIMARY : COLORS.TEXT,
              }}
            >
              <Icon
                name={item.icon}
                size={18}
                color={isActive ? COLORS.PRIMARY : COLORS.TEXT}
              />
              <Typography sx={TYPOGRAPHY.BODY_LARGE}>{item.label}</Typography>
            </MenuItem>
          );
        })}
      </Menu>

      {/* Engineer Details Sidebar */}
      <EngineerDetailsSidebar
        open={isSidebarOpen}
        onClose={() => {
          setIsSidebarOpen(false);
          setSelectedEngineer(null);
        }}
        engineer={selectedEngineer}
      />

      {/* Status Update Modal */}
      <EngineerStatusUpdateModal
        open={statusModalOpen}
        engineer={selectedEngineer}
        onClose={() => {
          setStatusModalOpen(false);
          setSelectedEngineer(null);
        }}
        onUpdate={handleStatusUpdate}
      />

      {/* Delete Modal */}
      <EngineerDeleteModal
        open={deleteOpen}
        engineer={selectedEngineer}
        onClose={() => {
          setDeleteOpen(false);
          setSelectedEngineer(null);
        }}
        onConfirmDelete={handleConfirmDelete}
      />

      <EngineerEditModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedEngineer(null);
        }}
        engineer={selectedEngineer}
        onUpdate={(updatedEngineer) => {
          // Update the engineer in the list
          setEngineersData((prev) =>
            prev.map((eng) =>
              eng._id === updatedEngineer._id ? updatedEngineer : eng,
            ),
          );
          showToast(
            "success",
            "Updated",
            `${updatedEngineer.name}'s details have been updated`,
          );
        }}
        showToast={showToast}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Toast Component */}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={hideToast}
        autoHideDuration={5000}
      />
    </Box>
  );
};

export default Engineers;
